/**
 * SPA entry point. Loads the runtime `/config.json`, then mounts the app wrapped in the config
 * provider, the OIDC AuthProvider (Cognito), and the router. Config is loaded before render so the
 * auth provider and API client are configured from real values, never hardcoded ones.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App';
import { buildOidcConfig } from './auth/oidc-config';
import { loadRuntimeConfig } from './config';
import { ConfigProvider } from './config-context';

import './styles.css';

async function bootstrap(): Promise<void> {
  const container = document.getElementById('root');
  if (container === null) {
    throw new Error('Root container #root not found');
  }
  const root = createRoot(container);

  try {
    const config = await loadRuntimeConfig();
    root.render(
      <StrictMode>
        <ConfigProvider config={config}>
          <AuthProvider {...buildOidcConfig(config)}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AuthProvider>
        </ConfigProvider>
      </StrictMode>,
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    root.render(
      <div className="auth-screen">
        <div className="auth-card">
          <h1>Configuration error</h1>
          <p className="auth-error">{message}</p>
          <p className="muted">
            The dashboard could not load its runtime configuration (<code>/config.json</code>).
          </p>
        </div>
      </div>,
    );
  }
}

void bootstrap();
